import { useCallback, useMemo } from 'react'

import Button from '@mui/material/Button'

import DatePickerPopover, { DatePickerPopoverProps } from '../DatePickerPopover/DatePickerPopover';
import DebouncedTextField from '../DebouncedTextField'
import { arrayDiff, arrayUnion, isPresent } from '../../lib/utils'
import sortBy from 'lib/utils/sort-by'
import Checkbox from '../controls/Checkbox'
import CheckboxMenu, { CheckboxMenuProps } from '../controls/CheckboxMenu'

import { useDataManager } from './DataManager'
import useStyles from './styles'

import FilterActiveImage from './assets/filter-active.png'
import FilterInactiveImage from './assets/filter-inactive.png'
import DateActiveImage from './assets/date-active.png'
import DateInactiveImage from './assets/date-inactive.png'

import HoverPopover from 'material-ui-popup-state/HoverPopover'
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from 'material-ui-popup-state/hooks'
import { dateRangeToTimeRange, timeRangeToDateRange } from 'lib/time';
import { useTimezone } from 'lib/TimezoneProvider';
import { convertToAbsoluteTimeRange } from 'components/DatePickerPopover/dateRangeUtils';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import { Column, FieldColumn, GenericRow, LookupColumn } from './types';
import { TimeRange } from 'components/DatePickerPopover/types';
import PlainDate from 'lib/PlainDate';


function TextFilterField({column}) {
  const classes = useStyles()
  const dataManager = useDataManager()
  const hasFilter = isPresent(dataManager.filters[column.field]?.value?.[0])

  return (
    <DebouncedTextField
      size="small"
      className={`${hasFilter ? classes.filterFieldActive : ''}`}
      value={dataManager.filters[column.field]?.value?.[0] || ''}
      onChange={event => dataManager.setFilter(column.field, { operator: 'contains', value: event.target.value })}
      placeholder="Search..."
    />
  )
}

const EMPTY_ARRAY = []

function matchParentMenuOption(parentColumn, parentMenuOption, key) {
  const keys = parentColumn.subfieldMap[parentMenuOption[0]]
  return keys && keys.includes(key)
}

type LookupFilterMenuProps<RowType extends GenericRow> = CheckboxMenuProps<[string, string]> & {
  column: LookupColumn<RowType, unknown>
  parentColumn?: LookupColumn<RowType, unknown>
  parentMenuOption?: [string, string]
}

// TODO: there's some yuck in here; refactor by adding helper methods to DataManager for adding/removing filter values
// (e.g. we should be able to get back a guaranteed array from a dataManager.getFilterValues() method rather than having to guard every time)
function LookupFilterMenu<RowType extends GenericRow>({ column, parentColumn, parentMenuOption, ...menuProps }: LookupFilterMenuProps<RowType>) {
  const dataManager = useDataManager()
  const hasFilter = dataManager.filters[column.field]?.value instanceof Array && isPresent(dataManager.filters[column.field]?.value)
  const selectedKeys = hasFilter ? dataManager.filters[column.field]?.value : EMPTY_ARRAY
  const lookupOptions: [string, string][] = useMemo(() => {
    return sortBy(Object.entries(column.lookup).filter(([key, _value]) => parentColumn ? matchParentMenuOption(parentColumn, parentMenuOption, key) : true), ([_key, value]) => value)
  }, [column.lookup, parentColumn, parentMenuOption])
  const subfieldColumn = useMemo(() => dataManager.columns.find(someColumn => someColumn.field === column.subfield), [column.subfield, dataManager.columns]) as LookupColumn<RowType, unknown> | undefined
  const renderSubmenu = useMemo(() => {
    if(!subfieldColumn) {
      return undefined
    }
    // eslint-disable-next-line react/display-name
    return (subfieldMenuProps) => (
      <LookupFilterMenu parentColumn={column} column={subfieldColumn} Popover={HoverPopover} {...subfieldMenuProps}/>
    )
  }, [column, subfieldColumn])
  const renderLookupOption = useCallback(([_key, value]) => value, [])
  const isChecked = useCallback(([key, _value]) => selectedKeys.includes(key), [selectedKeys])
  const onChange = useCallback(([key, _value]: [string, string]) => event => {
    const newFilter = column.filterExclusive ? (event.target.checked ? [key] : []) : (event.target.checked ? arrayUnion(selectedKeys, [key]) : arrayDiff(selectedKeys, [key]))
    dataManager.setFilter(column.field, { operator: 'list', value: newFilter })
    if(subfieldColumn) {
      const subfieldSelectedKeys = dataManager.filters[subfieldColumn.field]?.value || EMPTY_ARRAY
      const subfieldKeys = Object.keys(subfieldColumn.lookup).filter(subfieldKey => matchParentMenuOption(column, [key], subfieldKey))
      dataManager.setFilter(subfieldColumn.field, { operator: 'list', value: event.target.checked ? arrayUnion(subfieldSelectedKeys, subfieldKeys) : arrayDiff(subfieldSelectedKeys, subfieldKeys) })
    }
    if(parentColumn && parentMenuOption) {
      const parentSelectedKeys = dataManager.filters[parentColumn.field]?.value || EMPTY_ARRAY
      if(event.target.checked) {
        dataManager.setFilter(parentColumn.field, { operator: 'list', value: arrayUnion(parentSelectedKeys, [parentMenuOption[0]]) })
      } else {
        // If every other subfield option was unchecked, then uncheck the parent
        if(!lookupOptions.some(([someKey, _someValue]) => someKey !== key && selectedKeys.includes(someKey))) {
          dataManager.setFilter(parentColumn.field, { operator: 'list', value: arrayDiff(parentSelectedKeys, [parentMenuOption[0]]) })
        }
      }
    }
  }, [column, selectedKeys, dataManager, subfieldColumn, parentColumn, parentMenuOption, lookupOptions])

  return lookupOptions.length > 0 && (
    <CheckboxMenu
      {...menuProps}
      menuOptions={lookupOptions}
      render={renderLookupOption}
      isChecked={isChecked}
      onChange={onChange}
      renderSubmenu={renderSubmenu}
    />
  )
}

function CheckboxFilterField({column}) {
  const dataManager = useDataManager()
  const handleChange = useCallback(event => {
    return dataManager.setFilter(column.field, { operator: 'equals', value: [event.target.checked] })
  }, [column, dataManager])

  return (
    <Checkbox
      onChange={handleChange}
      value={dataManager.filters[column.field]?.value?.[0]}
    />
  )
}

type DateFilterMenuProps<RowType extends GenericRow> = DatePickerPopoverProps & {
  column: FieldColumn<RowType, unknown>
}

function DateFilterMenu<RowType extends GenericRow>({column, ...popoverProps}: DateFilterMenuProps<RowType>) {
  const dataManager = useDataManager()
  const { timezone } = useTimezone()
  const handleChange = useCallback((value: TimeRange) => {
    const dateRange = timeRangeToDateRange(convertToAbsoluteTimeRange(value, timezone))
    const newFilter = dateRange === null ? null : { operator: 'between', value: dateRange as unknown as string[] } // TODO: the casting of dateRange as unknown as string[] is due to a bug in the type definition of Filter.
    dataManager.setFilter(column.field, newFilter)
  }, [column.field, dataManager, timezone])

  return (
    <DatePickerPopover
      {...popoverProps}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      onChange={(timeRange) => handleChange(timeRange)}
      initialValue={dateRangeToTimeRange((dataManager.filters[column.field]?.value ?? null) as unknown as ([PlainDate, PlainDate] | null), timezone)}
    />
  )
}

function FilterButton({ column, icon, activeIcon, component: Component }) {
  const classes = useStyles()
  const dataManager = useDataManager()
  const hasFilter = dataManager.hasFilter(column)
  const popupState = usePopupState({ variant: 'popover', popupId: 'filterField' })

  const handleClearFilter = (event) => {
    event.stopPropagation()
    dataManager.clearFilter(column)
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        className={`${classes.filterButton} ${hasFilter ? classes.filterButtonActive : ''}`}
        {...bindTrigger(popupState)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'relative',
          '& .clear-icon': {
            display: 'none',
          },
          '&:hover .filter-icon': {
            display: hasFilter ? 'none' : undefined,
          },
          '&:hover .clear-icon': {
            display: hasFilter ? 'inline-block' : 'none',
          },
        }}
      >
        <Box className="filter-icon" width={18} height={18} >
          <img
            alt={hasFilter ? 'active filter icon' : 'inactive filter icon'}
            src={hasFilter ? activeIcon : icon}
            width={18}
            height={18}
          />
        </Box>

        <Box className="clear-icon" width={18} height={18}  >
          <Tooltip title="Clear Filter">
            <CloseIcon
              onClick={(e) => handleClearFilter(e)}
              fontSize="medium"
              sx={{mt:-0.5, ml:-0.5}}
              aria-label={`Clear Filter ${column.title}`}
            />
          </Tooltip>
        </Box>

        Filter {column.title}
      </Button>

      <Component {...bindPopover(popupState)} column={column} />
    </>
  )
}

function LookupFilterField({column}) {
  return (
    <FilterButton column={column} icon={FilterInactiveImage} activeIcon={FilterActiveImage} component={LookupFilterMenu} />
  )
}

function DateFilterField({column}) {
  return (
    <FilterButton column={column} icon={DateInactiveImage} activeIcon={DateActiveImage} component={DateFilterMenu} />
  )
}

export type FilterFieldProps<RowType extends GenericRow> = {
  column: Column<RowType>
}

export default function FilterField<RowType extends GenericRow>({ column }: FilterFieldProps<RowType>) {
  if('lookup' in column) {
    return <LookupFilterField column={column}/>
  } else if(column.type === 'date' || column.type === 'datetime') {
    return <DateFilterField column={column}/>
  } else if(column.type == 'checkbox') {
    return <CheckboxFilterField column={column}/>
  } else {
    return <TextFilterField column={column}/>
  }
}
